import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { StatusBadge } from "../components/common/StatusBadge";
import { QuickActions } from "../components/common/QuickActions";
import { CallLogModal } from "../components/common/CallLogModal";
import { CallHistoryModal } from "../components/common/CallHistoryModal";
import { useAuth } from "../context/AuthContext";
import { apiRequest, formatPhone, formatDateTime } from "../lib/utils";
import { toast } from "sonner";

export const FollowUpsPage = () => {
  const { user, isAdmin } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callLogContact, setCallLogContact] = useState(null);
  const [historyContact, setHistoryContact] = useState(null);

  useEffect(() => {
    fetchFollowUps();
  }, []);

  const fetchFollowUps = async () => {
    try {
      let url = "/dashboard/follow-ups";
      if (!isAdmin() && user?.id) {
        url += `?cre_id=${user.id}`;
      }
      const data = await apiRequest(url);
      setLeads(data);
    } catch (error) {
      toast.error("Failed to fetch follow-ups");
    } finally {
      setLoading(false);
    }
  };

  const handleCall = (lead, skipDialer = false) => {
    // First open the phone dialer (unless skipped)
    if (!skipDialer && lead.phone) {
      window.location.href = `tel:${lead.phone}`;
    }
    // Show call log modal after a brief delay to allow dialer to open
    setTimeout(() => {
      setCallLogContact({
        id: lead.id,
        name: lead.name,
        phone: lead.phone,
        status: lead.status,
        type: "lead",
        branch_id: lead.branch_id
      });
    }, skipDialer ? 0 : 800);
  };

  const handleViewHistory = (lead) => {
    setHistoryContact({
      id: lead.id,
      name: lead.name,
      type: "lead"
    });
  };

  return (
    <div className="space-y-6" data-testid="follow-ups-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground">
          Today's Follow-ups
        </h1>
        <p className="text-muted-foreground">
          Leads scheduled for follow-up today
        </p>
      </div>

      <Card className="bento-card">
        <CardContent className="p-0">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-high-density">
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Follow-up Time</TableHead>
                  <TableHead>Service Interest</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No follow-ups scheduled for today
                    </TableCell>
                  </TableRow>
                ) : (
                  leads.map((lead) => (
                    <TableRow key={lead.id} className="table-high-density" data-testid={`followup-row-${lead.id}`}>
                      <TableCell className="font-medium">{lead.name}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatPhone(lead.phone)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={lead.status} />
                      </TableCell>
                      <TableCell className="text-sm font-mono">
                        {formatDateTime(lead.next_follow_up)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {lead.service_interest || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <QuickActions
                          phone={lead.phone}
                          onCall={() => handleCall(lead, true)}
                          onViewHistory={() => handleViewHistory(lead)}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <CallLogModal
        open={!!callLogContact}
        onClose={() => setCallLogContact(null)}
        contact={callLogContact}
        onSuccess={fetchFollowUps}
      />

      <CallHistoryModal
        open={!!historyContact}
        onClose={() => setHistoryContact(null)}
        contactId={historyContact?.id}
        contactType={historyContact?.type}
        contactName={historyContact?.name}
      />
    </div>
  );
};

export default FollowUpsPage;
